"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const Cropper = dynamic(() => import("react-easy-crop"), { ssr: false });
import { Button } from "@/components/revive-landing/ui/button";
import { cn } from "@/lib/revive-landing/utils";

export function ImageCropModal({
  open,
  imageSrc,
  onClose,
  onApply,
  aspect,
  outputWidth,
  outputHeight,
  title = "Crop image",
  description,
}) {
  const [mounted, setMounted] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [applying, setApplying] = useState(false);
  const croppedAreaPixelsRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      croppedAreaPixelsRef.current = null;
      setApplying(false);
    }
  }, [open]);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    croppedAreaPixelsRef.current = croppedAreaPixels;
  }, []);

  const handleApply = async () => {
    const pixels = croppedAreaPixelsRef.current;
    if (!pixels || !imageSrc) return;
    setApplying(true);
    try {
      await onApply(pixels);
    } finally {
      setApplying(false);
    }
  };

  if (!mounted || !open || !imageSrc) return null;

  const sizeLabel =
    outputWidth && outputHeight ? `${outputWidth}×${outputHeight}px` : null;
  const defaultDescription =
    sizeLabel && aspect
      ? `Frame matches ${sizeLabel} (${aspect === 1 ? "1:1" : `${aspect.toFixed(2)}:1`}). Drag to reposition, use zoom to fit.`
      : "Drag to reposition, use zoom to fit.";

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal
        aria-labelledby="image-crop-title"
        className="fixed left-1/2 top-1/2 z-[101] flex max-h-[min(90vh,720px)] w-[min(96vw,560px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-zinc-800 px-5 py-4">
          <div>
            <h2 id="image-crop-title" className="text-lg font-semibold text-zinc-50">
              {title}
            </h2>
            <p className="mt-1 text-sm text-zinc-500">{description ?? defaultDescription}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative h-[min(42vh,320px)] w-full bg-zinc-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={false}
            objectFit="contain"
          />
        </div>

        <div className="shrink-0 space-y-3 border-t border-zinc-800 px-5 py-4">
          <div className="flex items-center gap-3">
            <label htmlFor="image-crop-zoom" className="text-xs font-medium text-zinc-500">
              Zoom
            </label>
            <input
              id="image-crop-zoom"
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className={cn(
                "h-2 flex-1 cursor-pointer appearance-none rounded-full bg-zinc-800",
                "[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-500",
              )}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            <Button type="button" onClick={handleApply} disabled={applying}>
              {applying ? "Saving…" : "Apply crop"}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
